import React, { useState, useEffect } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { Alert, Box, Button, Typography } from "@mui/material";
import { useAuth } from "hooks/useAuth";
import useMfaStatus from "hooks/useMfaStatus";
import { useFdeRole } from "hooks/useFdeRole";
import { useGlobalTciManager } from "hooks/useGlobalTciManager";
import DashboardLayout from "components/dashboard/DashboardLayout";
import DashboardRoutes from "routes/DashboardRoutes";
import PatientRoutes from "routes/PatientRoutes";
import ProfessionalRoutes from "routes/ProfessionalRoutes";
import AdminRoutes from "routes/AdminRoutes";
import GlobalAdminRoutes from "routes/GlobalAdminRoutes";
import RegionalAdminRoutes from "routes/RegionalAdminRoutes";
import MarketingExecutiveRoutes from "routes/MarketingExecutiveRoutes";
import BtybMarketerRoutes from "routes/BtybMarketerRoutes";
import AccountNotSetUpPage from "components/auth/AccountNotSetUpPage";
import { ChatProvider } from "context/Chat";
import { db } from "services/firebase";
import Loading from "components/Loading";
import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import { shouldSkipMfaEnrollmentUi } from "utils/isDevEnvironment";
import { isDevAuthFirestoreUser } from "utils/isDevAuthFirestoreUser";
import {
  hasFacilityCardClaim,
  needsMfaEnrollment,
} from "utils/needsMfaEnrollment";
import { isDashboardIndexPath } from "features/msi3Teaching/msi3HostRouting";
import { useMsi3SignedInLanding } from "features/msi3Teaching/useMsi3SignedInLanding";
import { getDashboardRouterKey } from "pages/dashboard/getDashboardRouterKey";

const DashboardPage = () => {
  const { user, userLoading, userData, isGlobalAdmin, tokenClaims } = useAuth();
  const { isEnrolled, sessionMfaLoading } = useMfaStatus();
  const { isFdeDc, isFdeC, isFdeT, loading: fdeLoading } = useFdeRole();
  const { isGlobalTciManager, loading: gtmLoading } = useGlobalTciManager();
  const hasGtmAccess = isGlobalTciManager || Boolean(tokenClaims?.globalTciManager);
  const isFacilityCardUser = hasFacilityCardClaim(tokenClaims);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const atDashboardIndex = isDashboardIndexPath(pathname);
  const msi3Landing = useMsi3SignedInLanding({ enabled: atDashboardIndex });
  const [isLoading, setIsLoading] = useState(true);
  const [userDocChecked, setUserDocChecked] = useState(false);

  // Effect to handle authentication and data loading
  useEffect(() => {
    const checkUserStatus = async () => {
      // Wait for authentication to complete
      if (userLoading) return;

      // If no user, we'll handle redirect in the render
      if (!user) {
        setIsLoading(false);
        setUserDocChecked(true);
        return;
      }

      // If userData is already loaded through useAuth hook
      if (userData) {
        setIsLoading(false);
        setUserDocChecked(true);
        return;
      }

      // If we have a user but no userData, check Firestore directly
      try {
        const userDoc = await db.collection("users").doc(user.uid).get();
        const userData = userDoc.data();

        if (!userDoc.exists) {
          // Redirect to onboarding if no user document
          navigate("/onboarding");
        } else if (userData.onboarding) {
          // If user is still in onboarding, redirect back to onboarding
          navigate("/onboarding");
        } else {
          // We've confirmed the document exists and onboarding is complete
          setIsLoading(false);
          setUserDocChecked(true);
        }
      } catch (error) {
        console.error("Error checking user document:", error);
        setIsLoading(false);
        setUserDocChecked(true);
      }
    };

    checkUserStatus();
  }, [user, userLoading, userData]);

  // If still loading or user document hasn't been checked, show loading screen
  if (isLoading || !userDocChecked || fdeLoading || gtmLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <Loading page />
      </Box>
    );
  }

  // If user is not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/auth" />;
  }

  if (
    !sessionMfaLoading &&
    needsMfaEnrollment({
      user,
      isEnrolled,
      skipUi: shouldSkipMfaEnrollmentUi(),
      claims: tokenClaims,
    })
  ) {
    return <Navigate to="/onboarding" />;
  }

  if (sessionMfaLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <Loading page />
      </Box>
    );
  }

  // Safety-net: unverified email users who slipped past the AuthFlow gate.
  // Dev auth codes create @dev.local accounts via the client SDK, which cannot
  // set emailVerified — skip this gate for those synthetic accounts.
  // Facility-card accounts have no email; skip via the Auth custom claim only.
  if (
    !user.emailVerified &&
    !isDevAuthFirestoreUser(userData) &&
    !isFacilityCardUser
  ) {
    const resend = async () => {
      try {
        await firebase.auth().currentUser.sendEmailVerification();
      } catch (e) {
        /* ignore */
      }
    };
    return (
      <DashboardLayout>
        <Typography variant="h5" sx={{ mt: { xs: 4, sm: 8 }, mb: 2 }}>
          Confirm your email
        </Typography>
        <Alert severity="warning" sx={{ mb: 3 }}>
          Please verify your email address before continuing. Check your inbox
          for a link from us.
        </Alert>
        <Button variant="contained" onClick={resend}>
          Resend Verification Email
        </Button>
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Need help? Email{" "}
            <a href="mailto:support@md3c.com">support@md3c.com</a>
          </Typography>
        </Box>
      </DashboardLayout>
    );
  }

  // If we have userData but no role, redirect to onboarding. VoxPont users have
  // no HealthDesk role by design, so send them to the VoxPont product picker
  // instead of the FusionMSI account-type onboarding.
  const routerKey = getDashboardRouterKey({
    userData,
    isGlobalAdmin,
    isFdeDc,
    isFdeC,
    isFdeT,
    hasGtmAccess,
  });
  if (routerKey === "onboarding") {
    navigate(userData.product === "voxpont" ? "/voxpont-start" : "/onboarding");
    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          overflow: "hidden",
        }}
      >
        <Loading page />
      </Box>
    );
  }

  // A provider who came from a /share link to sign in lands here once fully
  // authed; bounce them back to the share page (now signed in) so they can view
  // the record and push it into ChartMind. The key is cleared on the /share side
  // (ShareLandingPage mount) so this render stays pure. Only honor /share paths.
  const shareReturn = localStorage.getItem("pendingShareReturn");
  if (shareReturn && shareReturn.startsWith("/share")) {
    return <Navigate to={shareReturn} replace />;
  }

  // msi3 host: default /dashboard index → faculty cases or AcuityDrill.
  // Deep links under /dashboard/* are left alone.
  if (msi3Landing.active && atDashboardIndex) {
    if (msi3Landing.loading || !msi3Landing.path) {
      return (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            overflow: "hidden",
          }}
        >
          <Loading page />
        </Box>
      );
    }
    return <Navigate to={msi3Landing.path} replace />;
  }

  return (
    <ChatProvider>
      {routerKey === "globalAdmin" ? (
        <GlobalAdminRoutes />
      ) : routerKey === "regionalAdmin" ? (
        <RegionalAdminRoutes />
      ) : routerKey === "regionalAdminMissingRegion" ? (
        <DashboardLayout>
          <Alert severity="warning" sx={{ m: 2 }}>
            Your account is missing <strong>region</strong> on your user
            document. An administrator must set it to match regional prompt
            scope (any stable string).
          </Alert>
        </DashboardLayout>
      ) : routerKey === "patient" ? (
        <PatientRoutes />
      ) : routerKey === "institutionAdmin" ? (
        <AdminRoutes />
      ) : routerKey === "professional" ? (
        <ProfessionalRoutes />
      ) : routerKey === "marketingExecutive" ? (
        <MarketingExecutiveRoutes />
      ) : routerKey === "btybMarketer" ? (
        <BtybMarketerRoutes />
      ) : routerKey === "facility" ? (
        <DashboardRoutes />
      ) : (
        <AccountNotSetUpPage />
      )}
    </ChatProvider>
  );
};

export default DashboardPage;
